import { useEffect, useRef, useState } from "react";
import type { StoreApi, UseBoundStore } from "zustand";
import { useChatStore, type ChatState } from "../stores/chatStore";
import { useConversationMessages } from "./useConversations";

interface UseConversationSessionOptions {
  sessionId?: string | null;
  initialMessage?: string;
  sendMessage: (content: string) => void;
  store?: UseBoundStore<StoreApi<ChatState>>;
}

export function useConversationSession({
  sessionId,
  initialMessage,
  sendMessage,
  store,
}: UseConversationSessionOptions) {
  const useStore = store ?? useChatStore;
  const prevSessionId = useRef<string | null>(null);
  const historyLoaded = useRef(false);
  const [historySessionId, setHistorySessionId] = useState<string | undefined>(
    undefined,
  );

  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;

  const conversationQuery = useConversationMessages(historySessionId);

  useEffect(() => {
    const storeState = useStore.getState();

    if (!sessionId) {
      if (prevSessionId.current !== null) {
        prevSessionId.current = null;
        historyLoaded.current = false;
        storeState.clearMessages();
      }

      setHistorySessionId(undefined);
      return;
    }

    const sessionChanged = prevSessionId.current !== sessionId;
    if (sessionChanged) {
      prevSessionId.current = sessionId;
      historyLoaded.current = false;
      storeState.clearMessages({ sessionId });
    }

    if (initialMessage) {
      setHistorySessionId(undefined);
      const refreshedState = useStore.getState();
      if (refreshedState.messages.length === 0) {
        const timeoutId = setTimeout(() => {
          void sendMessageRef.current(initialMessage);
        }, 0);
        return () => clearTimeout(timeoutId);
      }
      return;
    }

    setHistorySessionId(sessionId);
  }, [initialMessage, sessionId, useStore]);

  useEffect(() => {
    if (conversationQuery.data?.messages && !historyLoaded.current) {
      const storeState = useStore.getState();
      if (storeState.messages.length === 0) {
        historyLoaded.current = true;
        storeState.loadMessages(conversationQuery.data.messages);
      }
    }
  }, [conversationQuery.data, useStore]);

  return {
    isHistoryLoading: conversationQuery.isLoading && Boolean(sessionId),
  };
}
